import React, { useEffect, useRef, useState } from 'react';
import { apiPost, apiPut, apiThrow } from '../utils/api';
import { BTN } from '../constants/buttons';
import { ROLE_LABELS } from '../constants/statuses';
import { emailInUseHandoffMessage, isEmailInUse } from '../constants/emailInUse';
import useGrantableRoles from '../hooks/useGrantableRoles';
import useSubmitGuard from '../hooks/useSubmitGuard';
import useIdempotencyKey from '../hooks/useIdempotencyKey';
import { friendlyErrorMessage } from './ErrorMessage';
import { sendDepartmentInvite } from '../utils/departmentInvite';
import InfoTip from './InfoTip';

/**
 * THE ONE "+ Add person" FORM (owner ruling, 2026-09-24). It replaced two
 * buttons and two forms — "+ Add Person" and "Invite by email" — after Pete:
 * *"Invite by email and Add person seem the same. Can they not just be the same
 * button? then we have Add and invite or add & invite later?"*
 *
 * Fields: Name *, Email (optional), Department, Role. Two actions:
 *  - **Add & send invite** — `POST /api/invitations` (manager-only, R24;
 *    adopt-or-OFFER: a new address gets a set-password email, an address that
 *    already has an account gets a "Join this department?" prompt, never a
 *    duplicate). Carries its own Idempotency-Key.
 *  - **Add, invite later** — `POST /api/users` (a placeholder; the server seats
 *    it as `user` in the department named as `department_id`, else the caller's
 *    ACTIVE one — 2026-11-03) plus the seat role, and no
 *    email. The row's own Invite button sends it later. This is the FORM's
 *    submit, so an accidental Enter can never email anybody.
 *
 * 🔑 THE PAGE DECIDES WHO SEES WHICH ACTION (`canSendInvite` / `canAddLater`),
 * each mirroring its server route; this component resolves no authority. The
 * Role picker offers EXACTLY the server's `grantable_roles` for the department
 * (department roles only — there is no unscoped or site role anywhere here,
 * owner ruling (d) 2026-09-24).
 *
 * 🔑 THE DEPARTMENT IS FIXED: `fixedDepartment` when the "+ Add person" panel
 * (`AddPersonPanel.js`) opens it for a known department, else the one the caller
 * stands in. A site admin with neither stands in none, so they pick one. Both
 * routes are told the department explicitly; neither guesses.
 *
 * There is no Status dropdown: status follows the action (placeholder, or
 * invited by the invite route).
 */

export const ADD_PERSON_EMAIL_TAKEN =
  'That email already belongs to a CrewForge account. Use “Add & send invite” to ask them to join instead.';

export const TIP_SEND_INVITE =
  'Emails them now. A new person gets a link to set their password. If they already use CrewForge, they get a “Join this department?” prompt instead.';
export const TIP_INVITE_LATER =
  'Adds them to your list without emailing anyone. Send the invite later from their row.';
export const TIP_EMAIL = 'Optional, but you need it to send an invite.';
export const TIP_ROLE = 'These are the roles you are allowed to give in this department.';
export const TIP_DEPARTMENT_FIXED =
  'They are added to the department you are working in. Switch department to add someone elsewhere.';
export const TIP_DEPARTMENT_PICK = 'The department they join.';
export const TIP_DEPARTMENT_GIVEN = 'They join this department.';
// Design pass (LR2, 2026-09-24): a disabled "Add & send invite" says WHY on the
// control itself, and one muted line under the buttons names both routes.
export const SEND_INVITE_NEEDS_EMAIL = 'Add an email address to send an invite';

/**
 * The ONE string behind a disabled "Add & send invite"'s `title` AND its
 * `sr-only` description — what is really missing, or `null` when nothing is
 * (POL2 A5, 2026-09-25). It used to name only the email, so a site admin with
 * an email typed and no department picked was shown the positive action tip
 * on a button that would not press. Email-only reads exactly
 * `SEND_INVITE_NEEDS_EMAIL`, as before.
 */
export function sendInviteMissingReason({ name, email, department }) {
  const missing = [];
  if (!name) missing.push('a name');
  if (!email) missing.push('an email address');
  if (!department) missing.push('a department');
  if (missing.length === 0) return null;
  const list = missing.length === 1
    ? missing[0]
    : `${missing.slice(0, -1).join(', ')} and ${missing[missing.length - 1]}`;
  return `Add ${list} to send an invite`;
}
export const ADD_LATER_HINT = 'Add an email to send an invite, or add them now and invite later.';

const inputCls = 'w-full min-h-[44px] md:min-h-0 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500';
const labelCls = 'text-xs font-medium text-gray-600 dark:text-gray-400 mb-1 flex items-center gap-1';
const roleLabel = (role) => ROLE_LABELS[role] || role;

// Bring newly shown content into view — on the EVENT that showed it, never on a
// timer, and only if it is not already visible (`nearest`). The scroll margin
// on each target (`scroll-mb-24 lg:scroll-mb-4`) clears the 65px phone bottom
// nav. Demo polish 2026-09-25: the form opened below the fold on a phone and
// its helper line sat behind that nav. jsdom has no `scrollIntoView`, hence the
// guard.
const SCROLL_TARGET_MARGIN = 'scroll-mt-20 scroll-mb-24 lg:scroll-mb-4';
function revealNearest(el) {
  if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
}

/**
 * `<option>`s for a department picker: "Name — Production". ONE copy, shared
 * with PeopleManager's per-row "Add to department" picker.
 */
export function DepartmentOptions({ departments }) {
  return (departments || []).map((d) => (
    <option key={d.id} value={d.id}>
      {d.name}{d.production_name ? ` — ${d.production_name}` : ''}
    </option>
  ));
}

export default function AddPersonForm({
  isGlobalAdmin,
  activeDepartment,
  allDepartments,
  canSendInvite,
  canAddLater,
  onClose,
  onDone,
  fixedDepartment = null,
  initialName = '',
  initialEmail = '',
}) {
  const activeId = Number(activeDepartment?.department_id);
  const activeIsReal = !!activeDepartment && !activeDepartment.production_is_personal
    && Number.isInteger(activeId) && activeId > 0;
  const [form, setForm] = useState({
    name: initialName,
    email: initialEmail,
    departmentId: activeIsReal ? String(activeId) : '',
    role: '',
  });
  const [error, setError] = useState('');

  // `fixedDepartment` (2026-11-03): the one "+ Add person" panel opens this form
  // for a department it already knows (a department card's, or the one the
  // caller stands in), admin and non-admin alike, so there is no picker.
  const pickDepartment = isGlobalAdmin && !fixedDepartment;
  const departmentId = fixedDepartment ? Number(fixedDepartment.id)
    : (isGlobalAdmin ? Number(form.departmentId) || null : (activeIsReal ? activeId : null));
  const dept = fixedDepartment || (isGlobalAdmin
    ? (allDepartments || []).find((d) => Number(d.id) === departmentId) || null
    : (activeIsReal ? {
      id: activeId,
      name: activeDepartment.department_name,
      production_id: activeDepartment.production_id,
      production_name: activeDepartment.production_name,
    } : null));

  // ── The roles this caller may give in THAT department ─────────────────────
  // A refusal means the caller may not ASSIGN roles here: "Add, invite later"
  // still seats them as `user`, which the fallback below offers.
  const grantable = useGrantableRoles(departmentId);
  const roles = grantable.length > 0 ? grantable : ['user'];
  const role = roles.includes(form.role) ? form.role : (roles.includes('user') ? 'user' : roles[0]);

  const { guard, busy } = useSubmitGuard();
  const createIdem = useIdempotencyKey();
  const inviteIdem = useIdempotencyKey();

  const name = form.name.trim();
  const email = form.email.trim();
  const inviteReady = canSendInvite && !!name && !!email && !!dept && grantable.includes(role);
  const laterReady = canAddLater && !!name;
  const showInviteHint = canSendInvite && canAddLater && !!name && !email;
  const sendMissingReason = sendInviteMissingReason({ name, email, department: dept });

  const formRef = useRef(null);
  const hintRef = useRef(null);
  const nameRef = useRef(null);
  const emailRef = useRef(null);
  const prefillRef = useRef({ initialName, initialEmail }); // as first rendered
  useEffect(() => {
    const { initialName: n, initialEmail: e } = prefillRef.current;
    if (!n && !e) return;
    const target = n ? emailRef.current : nameRef.current;
    if (target) target.focus();
  }, []);
  useEffect(() => { revealNearest(formRef.current); }, []);
  useEffect(() => { if (showInviteHint) revealNearest(hintRef.current); }, [showInviteHint]);

  const finish = (message, tone) => {
    if (onDone) onDone(message, tone);
    if (onClose) onClose();
  };

  // ── Add & send invite ──────────────────────────────────────────────────────
  const handleSendInvite = () => {
    if (!inviteReady) return;
    guard(async () => {
      try {
        setError('');
        const result = await sendDepartmentInvite({
          email, name, role, dept,
          requestOptions: { headers: { 'Idempotency-Key': inviteIdem.mint() } },
        });
        inviteIdem.clear();
        finish(result.message, result.tone);
      } catch (err) {
        setError(friendlyErrorMessage(err));
      }
    });
  };

  // ── Add, invite later ──────────────────────────────────────────────────────
  const handleAddLater = (e) => {
    if (e) e.preventDefault();
    if (!laterReady) return;
    guard(async () => {
      try {
        setError('');
        // The server gates on and seats into the NAMED department (2026-11-03),
        // so a department card's "invite later" lands in that card's department.
        const res = await apiPost('/api/users', {
          display_name: name,
          ...(email ? { email } : {}),
          ...(dept ? { department_id: Number(dept.id) } : {}),
        }, { headers: { 'Idempotency-Key': createIdem.mint() } });
        const data = await res.json();
        if (!res.ok) {
          if (isEmailInUse(data)) throw new Error(emailInUseHandoffMessage(data));
          // Add Person's plain 409 carries no code (server/routes/users.js POST /).
          if (res.status === 409 && !data.code && email && canSendInvite) throw new Error(ADD_PERSON_EMAIL_TAKEN);
          throw new Error(data.error || 'Failed to add person');
        }
        createIdem.clear();
        const newUserId = data.id || data.user_id;

        // The seat. The create seated them `user` in `dept` (named above, for
        // a site admin too); only a different role needs a second write.
        let seatWarning = '';
        try {
          if (dept && newUserId) {
            if (role !== 'user') {
              const r = await apiPut(`/api/departments/${dept.id}/members/${newUserId}`, { role });
              if (!r.ok) await apiThrow(r);
            }
            window.dispatchEvent(new Event('sfx-role-change'));
          }
        } catch (seatErr) {
          seatWarning = friendlyErrorMessage(seatErr);
        }
        if (seatWarning) {
          finish(`"${data.display_name || name}" added, but their role was not set: ${seatWarning}`, 'warning');
        } else {
          finish(`"${data.display_name || name}" added. Send their invite later from their row.`, 'success');
        }
      } catch (err) {
        setError(friendlyErrorMessage(err));
      }
    });
  };

  return (
    <form
      ref={formRef}
      onSubmit={handleAddLater}
      data-testid="add-person-form"
      className={`${SCROLL_TARGET_MARGIN} mb-4 p-4 bg-gray-50 dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 space-y-3`}
    >
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
        <div>
          <label htmlFor="add-person-name" className={labelCls}>Name *</label>
          <input id="add-person-name" ref={nameRef} type="text" required value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            className={inputCls} placeholder="e.g. John Smith" data-testid="add-person-name" />
        </div>
        <div>
          <label htmlFor="add-person-email" className={labelCls}>
            Email
            <InfoTip title={TIP_EMAIL} />
          </label>
          <input id="add-person-email" ref={emailRef} type="email" value={form.email}
            onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))}
            className={inputCls} placeholder="Optional" data-testid="add-person-email" />
        </div>
        <div>
          <label htmlFor="add-person-department" className={labelCls}>
            Department
            <InfoTip title={pickDepartment ? TIP_DEPARTMENT_PICK : (fixedDepartment ? TIP_DEPARTMENT_GIVEN : TIP_DEPARTMENT_FIXED)} />
          </label>
          {pickDepartment ? (
            <select id="add-person-department" value={form.departmentId}
              onChange={(e) => setForm((p) => ({ ...p, departmentId: e.target.value, role: '' }))}
              className={inputCls} data-testid="add-person-department">
              <option value="">-- None --</option>
              <DepartmentOptions departments={(allDepartments || []).filter((d) => !d.is_personal)} />
            </select>
          ) : (
            <p id="add-person-department" data-testid="add-person-department"
              // Read-only: styled as TEXT, not as an input (the browser pass found a
              // bordered white box read as editable).
              className="px-1 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 truncate"
              title={dept ? `${dept.name}${dept.production_name ? ` — ${dept.production_name}` : ''}` : undefined}>
              {dept ? dept.name : 'No department'}
            </p>
          )}
        </div>
        <div>
          <label htmlFor="add-person-role" className={labelCls}>
            Role
            <InfoTip title={TIP_ROLE} />
          </label>
          {roles.length === 1 ? (
            // One option is not a choice: muted read-only TEXT, the Department
            // field's idiom (a lone-option select reads as a broken picker).
            <p id="add-person-role" data-testid="add-person-role"
              className="px-1 py-2 text-sm text-gray-500 dark:text-gray-400">
              {roleLabel(roles[0])}
            </p>
          ) : (
            <select id="add-person-role" value={role}
              onChange={(e) => setForm((p) => ({ ...p, role: e.target.value }))}
              className={inputCls} disabled={!dept} data-testid="add-person-role">
              {roles.map((r) => <option key={r} value={r}>{roleLabel(r)}</option>)}
            </select>
          )}
        </div>
      </div>

      {/* Design pass (LR2): below `sm` every button is full width. The two
          ACTION tips are REQUIRED (owner): from `md` they are the buttons'
          `title` (hover); below `md` the ⓘ stays, because a title never shows
          without hover. Cancel carries an invisible ⓘ-sized spacer while the
          buttons stack, so all three are the same width. */}
      <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap sm:items-center sm:justify-end gap-2">
        <span className="flex w-full sm:w-auto items-center gap-1.5">
          <button type="button" onClick={onClose}
            className={`${BTN.secondary} min-h-[44px] md:min-h-0 w-full sm:w-auto justify-center`} data-testid="add-person-cancel">
            Cancel
          </button>
          <span aria-hidden="true" className="w-3.5 shrink-0 sm:hidden" />
        </span>
        {canAddLater && (
          <span className="flex w-full sm:w-auto items-center gap-1.5">
            <button type="submit" disabled={busy || !laterReady} title={TIP_INVITE_LATER}
              className={`${BTN.secondary} min-h-[44px] md:min-h-0 w-full sm:w-auto justify-center`}
              data-testid="add-person-invite-later">
              {busy ? 'Adding…' : 'Add, invite later'}
            </button>
            <span className="md:hidden inline-flex" data-testid="add-person-invite-later-tip">
              <InfoTip title={TIP_INVITE_LATER} align="right" />
            </span>
          </span>
        )}
        {canSendInvite && (
          <span className="flex w-full sm:w-auto items-center gap-1.5">
            <button type="button" onClick={handleSendInvite} disabled={busy || !inviteReady}
              title={sendMissingReason || TIP_SEND_INVITE}
              aria-describedby={sendMissingReason ? 'add-person-send-invite-reason' : undefined}
              className={`${BTN.primary} min-h-[44px] md:min-h-0 w-full sm:w-auto justify-center`}
              data-testid="add-person-send-invite">
              {busy ? 'Sending…' : 'Add & send invite'}
            </button>
            {sendMissingReason && <span id="add-person-send-invite-reason" className="sr-only">{sendMissingReason}</span>}
            <span className="md:hidden inline-flex" data-testid="add-person-send-invite-tip">
              <InfoTip title={TIP_SEND_INVITE} align="right" />
            </span>
          </span>
        )}
      </div>
      {showInviteHint && (
        <p ref={hintRef} className={`${SCROLL_TARGET_MARGIN} text-xs text-gray-500 dark:text-gray-400 sm:text-right`} data-testid="add-person-invite-hint">
          {ADD_LATER_HINT}
        </p>
      )}
      {error && (
        <p role="alert" className="text-sm text-red-700 dark:text-red-300" data-testid="add-person-error">{error}</p>
      )}
    </form>
  );
}
